/**
 * Configures the API based on the environment.
 */


interface Config {
  readonly SESSION_API_URL: string;
  readonly PROFILE_API_URL: string;
  readonly REPORTING_API_URL: string;
  readonly BACKEND_API_URL?: string; // Optional, if notifications API is separate
  readonly V2_REPORTING_API_URL?: string; // New reporting backend (Aspire-hosted)
  readonly BILLING_API_URL?: string; // Billing service (Aspire-hosted); dev-only until deployed
  readonly ADMIN_API_URL?: string; // MSB admin API (XLogs.Backend.Admin, Aspire-hosted)
}

// local
const LOCAL_SESSION = "https://localhost:7113";
const LOCAL_PROFILE = "https://localhost:7158";
const LOCAL_REPORTING = "https://localhost:7151";
const LOCAL_V2_REPORTING = "http://localhost:5022";
const LOCAL_BILLING = "http://localhost:5194";
const LOCAL_ADMIN = "http://localhost:5024";

//development
const LOCAL_DEV_SESSION = "https://xng-sessionapi-dev.azurewebsites.net";
const LOCAL_DEV_PROFILE = "https://xng-profileapi-dev.azurewebsites.net";
const LOCAL_DEV_REPORTING = "https://xng-api-ms.azure-api.net/api/dev-reporting";

//DBR
const LOCAL_DBR = "http://localhost:5193";
const DBR_NOTIFICATIONS = "http://localhost:5193";

//  dev api path
const DEV_SESSION = "/api/dev-session";
const DEV_PROFILE = "/api/dev-profile";
const DEV_REPORTING = "/api/dev-reporting";

// uat api path
const UAT_SESSION = "https://xng-uat.azurewebsites.net";
const UAT_PROFILE = "https://xng-uat.azurewebsites.net";
const UAT_REPORTING = "https://api-xng-reporting-scus-tx-newprod-001-staging.azurewebsites.net";
const UAT_REPORTING_V2 = "https://xng-reporting-uat.azurewebsites.net";
const UAT_NOTIFICATIONS = "https://xng-uat.azurewebsites.net";
const UAT_BILLING = "https://xng-billing-uat.azurewebsites.net";
const UAT_ADMIN = "https://xng-admin-uat-c2d6h7f5ahbfc5gu.southcentralus-01.azurewebsites.net";

// staging api path
const STAGING_SESSION = "/api/staging-session";
const STAGING_PROFILE = "/api/staging-profile";
const STAGING_REPORTING = "/api/staging-reporting";

// dbr-staging
const DBR_STAGING = "https://xng-backend-staging.azurewebsites.net";
const DBR_STAGING_REPORTING = "/api/staging-reporting";

// dbr-production
const DBR_PRODUCTION = "https://xng-backend.azurewebsites.net";
const DBR_PRODUCTION_REPORTING = "/api/reporting";
// production api path
const PROD_SESSION = "/api/session";
const PROD_PROFILE = "/api/profile";
const PROD_REPORTING = "/api/reporting";
const PROD_REPORTING_V2 = "https://app-xng-reporting-prod.azurewebsites.net";
const PROD_ADMIN = "https://xng-admin-prod-hpdjfrckhsbygvf3.southcentralus-01.azurewebsites.net";

export enum MODE {
  LOCAL = "local",
  DBR = "dbr",
  LOCAL_FRONTEND_ONLY = "local-frontend-only",
  LOCAL_FRONTEND_AND_SESSION_API = "local-frontend-and-session-api",
  LOCAL_FRONTEND_AND_PROFILE_API = "local-frontend-and-profile-api",
  LOCAL_FRONTEND_AND_REPORTING_API = "local-frontend-and-reporting-api",
  LOCAL_FULL_STACK = "local-full-stack",
  DEVELOPMENT = "development",
  UAT = "uat",
  STAGING = "staging",
  DBR_STAGING = "dbr-staging",
  DBR_PRODUCTION = "dbr-production",
  PRODUCTION = "production",
}

export class GlobalLogger {
  private static instance: GlobalLogger;
  devStyles: string = "color: green; font-weight: bold; font-size: 20px;";
  prodStyles: string = "color: red; font-weight: bold; font-size: 20px;";
  infoStyles: string = "color: yellow; font-weight: bold; font-size: 20px;";
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor() {}
  static getInstance(): GlobalLogger {
    if (!this.instance) {
      this.instance = new GlobalLogger();
    }
    return this.instance;
  }
  developmentModelogs(message: string) {
    console.log("%c=========================", "color: blue");
    console.log("%c=========================", "color: blue");
    console.log(`%c${message.toUpperCase()}`, this.devStyles);
    console.log("%c=========================", "color: blue");
    console.log("%c=========================", "color: blue");
  }

  productionModelogs(message: string) {
    console.log("%c=========================", "color: red");
    console.log("%c=========================", "color: red");
    console.log(`%c${message.toUpperCase()}`, this.prodStyles);
    console.log("%c=========================", "color: red");
    console.log("%c=========================", "color: red");
  }

  info(message: string) {
    console.log("%c=========================", "color: yellow");
    console.log("%c=========================", "color: yellow");
    console.log(`%c${message.toUpperCase()}`, this.infoStyles);
    console.log("%c=========================", "color: yellow");
    console.log("%c=========================", "color: yellow");
  }
}

/**
 * Represents the API configuration manager.
 */
export class APIConfigManager {
  /**
   * The configuration object.
   */
  readonly config: Config;

  /**
   * The mode of the API configuration.
   */
  readonly MODE: MODE;

  /**
   * The singleton instance of the APIConfigManager.
   */
  private static instance: APIConfigManager;

  /**
   * Constructs a new instance of the APIConfigManager.
   * Private to enforce singleton pattern.
   */
  private constructor() {
    this.MODE = process.env.REACT_APP_BRANCH as MODE;

    switch (this.MODE) {
      case MODE.LOCAL:
      case "local-frontend-only":
        this.config = {
          PROFILE_API_URL: LOCAL_DEV_PROFILE,
          SESSION_API_URL: LOCAL_DEV_SESSION,
          REPORTING_API_URL: LOCAL_DEV_REPORTING,
          V2_REPORTING_API_URL: LOCAL_V2_REPORTING,
          BILLING_API_URL: LOCAL_BILLING,
          ADMIN_API_URL: LOCAL_ADMIN,
        };
        GlobalLogger.getInstance().developmentModelogs(`running on ${this.MODE} env`);
        break;
      case "local-frontend-and-session-api":
        this.config = {
          PROFILE_API_URL: LOCAL_DEV_PROFILE,
          SESSION_API_URL: LOCAL_SESSION,
          REPORTING_API_URL: LOCAL_DEV_REPORTING,
          V2_REPORTING_API_URL: LOCAL_V2_REPORTING,
          BILLING_API_URL: LOCAL_BILLING,
          ADMIN_API_URL: LOCAL_ADMIN,
        };
        GlobalLogger.getInstance().developmentModelogs(`running on ${this.MODE} env`);
        break;
      case "local-frontend-and-profile-api":
        this.config = {
          PROFILE_API_URL: LOCAL_PROFILE,
          SESSION_API_URL: LOCAL_DEV_SESSION,
          REPORTING_API_URL: LOCAL_DEV_REPORTING,
          V2_REPORTING_API_URL: LOCAL_V2_REPORTING,
          BILLING_API_URL: LOCAL_BILLING,
          ADMIN_API_URL: LOCAL_ADMIN,
        };
        GlobalLogger.getInstance().developmentModelogs(`running on ${this.MODE} env`);
        break;
      case "local-frontend-and-reporting-api":
        this.config = {
          PROFILE_API_URL: LOCAL_DEV_PROFILE,
          SESSION_API_URL: LOCAL_DEV_SESSION,
          REPORTING_API_URL: LOCAL_REPORTING,
          V2_REPORTING_API_URL: LOCAL_V2_REPORTING,
          BILLING_API_URL: LOCAL_BILLING,
          ADMIN_API_URL: LOCAL_ADMIN,
        };
        GlobalLogger.getInstance().developmentModelogs(`running on ${this.MODE} env`);
        break;
      case "local-full-stack":
        this.config = {
          PROFILE_API_URL: LOCAL_PROFILE,
          SESSION_API_URL: LOCAL_SESSION,
          REPORTING_API_URL: LOCAL_DEV_REPORTING,
          V2_REPORTING_API_URL: LOCAL_V2_REPORTING,
          BILLING_API_URL: LOCAL_BILLING,
          ADMIN_API_URL: LOCAL_ADMIN,
        };
        GlobalLogger.getInstance().developmentModelogs(`running on ${this.MODE} env`);
        break;
      case "dbr":
        this.config = {
          PROFILE_API_URL: LOCAL_DBR,
          SESSION_API_URL: LOCAL_DBR,
          REPORTING_API_URL: LOCAL_DEV_REPORTING,
          BACKEND_API_URL: DBR_NOTIFICATIONS,
          V2_REPORTING_API_URL: LOCAL_V2_REPORTING,
          BILLING_API_URL: LOCAL_BILLING,
          ADMIN_API_URL: LOCAL_ADMIN,
        };
        GlobalLogger.getInstance().developmentModelogs(`running on ${this.MODE} env`);
        break;
      /*
       DEV API BASE URL
    */
      case "development":
        this.config = {
          PROFILE_API_URL: DEV_PROFILE,
          SESSION_API_URL: DEV_SESSION,
          REPORTING_API_URL: DEV_REPORTING,
        };
        GlobalLogger.getInstance().developmentModelogs(`running on ${this.MODE} env`);
        break;
              /*
       UAT API BASE URL
    */
      case "uat":
        this.config = {
          PROFILE_API_URL: UAT_PROFILE,
          SESSION_API_URL: UAT_SESSION,
          REPORTING_API_URL: UAT_REPORTING,
          BACKEND_API_URL: UAT_NOTIFICATIONS,
          V2_REPORTING_API_URL: UAT_REPORTING_V2,
          BILLING_API_URL: UAT_BILLING,
          ADMIN_API_URL: UAT_ADMIN,
        };
        GlobalLogger.getInstance().developmentModelogs(`running on ${this.MODE} env`);
        break;
      /*
       STAGING API BASE URL
    */
      case "staging":
        this.config = {
          PROFILE_API_URL: STAGING_PROFILE,
          SESSION_API_URL: STAGING_SESSION,
          REPORTING_API_URL: STAGING_REPORTING,
        };
        GlobalLogger.getInstance().developmentModelogs(`running on ${this.MODE} env`);
        break;

      case "dbr-staging":
        this.config = {
          PROFILE_API_URL: DBR_STAGING,
          SESSION_API_URL: DBR_STAGING,
          REPORTING_API_URL: DBR_STAGING_REPORTING,
          BACKEND_API_URL: DBR_STAGING,
          V2_REPORTING_API_URL: PROD_REPORTING_V2,
          ADMIN_API_URL: UAT_ADMIN,
        };
        GlobalLogger.getInstance().developmentModelogs(`running on ${this.MODE} env`);
        break;
      /*
       PRODUCTION API BASE URL
    */
      case "production":
        this.config = {
          PROFILE_API_URL: PROD_PROFILE,
          SESSION_API_URL: PROD_SESSION,
          REPORTING_API_URL: PROD_REPORTING,
        };
        break;

      case "dbr-production":
        this.config = {
          PROFILE_API_URL: DBR_PRODUCTION,
          SESSION_API_URL: DBR_PRODUCTION,
          REPORTING_API_URL: DBR_PRODUCTION_REPORTING,
          BACKEND_API_URL: DBR_PRODUCTION,
          V2_REPORTING_API_URL: PROD_REPORTING_V2,
          ADMIN_API_URL: PROD_ADMIN,
        };
        break;
      /*
       DEFAULT API BASE URL
    */
      default:
        this.config = {
          PROFILE_API_URL: PROD_PROFILE,
          SESSION_API_URL: PROD_SESSION,
          REPORTING_API_URL: PROD_REPORTING,
        };
        break;
    }
  }

  /**
   * Returns the singleton instance of the APIConfigManager.
   * @returns The APIConfigManager instance.
   */
  public static getInstance(): APIConfigManager {
    if (!APIConfigManager.instance) {
      APIConfigManager.instance = new APIConfigManager();
    }
    return APIConfigManager.instance;
  }
}
